/**
 * Roster Statistics Component
 * Displays total users and users who haven't logged in yet
 */

import { Box, Card, Typography, Skeleton } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import {
  People as PeopleIcon,
  PersonOff as PersonOffIcon,
} from '@mui/icons-material'

interface RosterStatisticsProps {
  totalUsers: number
  usersNotLoggedIn: number
  isLoading: boolean
}

interface StatCardProps {
  title: string
  value: string | number
  color: string
  icon?: React.ReactNode
}

function StatCard({ title, value, color, icon }: StatCardProps) {
  return (
    <Card
      className="p-6 text-center relative h-full flex flex-col justify-center"
      sx={{
        backgroundColor: 'background.paperDark',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {icon && (
        <Box className="flex justify-center mb-2">
          <Box
            className="rounded p-2 flex items-center justify-center"
            sx={{ bgcolor: color }}
          >
            {icon}
          </Box>
        </Box>
      )}
      <Typography
        variant="h3"
        className="font-bold mb-2"
        sx={{
          color,
          fontSize: '2.5rem',
        }}
      >
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {title}
      </Typography>
    </Card>
  )
}

export default function RosterStatistics({
  totalUsers,
  usersNotLoggedIn,
  isLoading,
}: RosterStatisticsProps) {
  let theme = useTheme()

  if (isLoading) {
    return (
      <Box className="mb-6">
        <Box className="flex items-center gap-2 mb-4">
          <Typography variant="h6">User Overview</Typography>
        </Box>
        <Box className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {[1, 2].map((i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={120}
              sx={{ borderRadius: 1 }}
            />
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Box className="mb-6">
      <Box className="flex items-center gap-2 mb-4">
        <Typography variant="h6">User Overview</Typography>
      </Box>

      <Box className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <StatCard
          title="Total Users"
          value={totalUsers}
          color={theme.palette.analyticsColors.userMetrics}
          icon={<PeopleIcon sx={{ color: 'white', fontSize: 20 }} />}
        />
        <StatCard
          title="Users Pending Initial Login"
          value={usersNotLoggedIn}
          color={theme.palette.analyticsColors.activities}
          icon={<PersonOffIcon sx={{ color: 'white', fontSize: 20 }} />}
        />
      </Box>
    </Box>
  )
}
